import { Link } from 'react-router-dom';
import { communesPrioritaires, communeUrl, type CommuneSEO } from '../data/communes';

/**
 * Bloc de liens contextuels vers les dix communes prioritaires.
 *
 * LOT 3 du plan SEO. Une page commune ne recevait de liens que depuis
 * /zones-intervention et quatre voisines : le signal s'arrêtait au premier
 * niveau. L'accueil et le pilier /demenagement — les deux pages les plus
 * fortes du site — n'envoyaient vers elles qu'une liste de pastilles à
 * l'ancre unique « Déménagement à X », qu'un moteur lit comme un pied de page.
 *
 * Ce bloc est de la PROSE : trois paragraphes qu'un lecteur peut lire, où
 * chaque commune apparaît avec ce qui la distingue réellement — sa distance,
 * son bâti, sa place dans l'histoire de l'entreprise — et où l'ancre du lien
 * varie d'une commune à l'autre. Les chiffres viennent de la table des
 * communes, jamais d'une valeur écrite ici : une distance corrigée en base se
 * corrige dans ces phrases.
 *
 * Deux variantes, parce que deux pages : l'accueil parle de territoire et de
 * proximité, le pilier parle de ce que l'intervention exige. Les phrases ne
 * sont pas les mêmes, pour ne pas servir cent cinquante mots identiques sur
 * les deux pages les plus fortes du site.
 *
 * Une commune dépubliée disparaît de la prose sans laisser de trou : chaque
 * phrase est autonome et n'est rendue que si sa commune est publiée.
 */

type Variante = 'accueil' | 'pilier';

/** « a, b et c » — les sections viennent de la table, pas d'une liste écrite ici. */
function enumerer(elements: string[]): string {
  if (elements.length <= 1) return elements[0] ?? '';
  return `${elements.slice(0, -1).join(', ')} et ${elements[elements.length - 1]}`;
}

/** Une phrase par commune, rendue avec son lien. `c` porte les mesures réelles. */
type Phrase = (c: CommuneSEO, lien: (ancre: string) => JSX.Element) => JSX.Element;

/** Paragraphes de l'accueil : le territoire vu depuis le dépôt. */
const ACCUEIL: Array<Record<string, Phrase>> = [
  {
    liege: (c, lien) => (
      <>Liège d'abord : votre {lien('déménagement à Liège')} se prépare quartier par quartier, du Laveu à Outremeuse, à {c.distanceDepotKm} km de notre dépôt. </>
    ),
    seraing: (c, lien) => (
      <>À Seraing, où se trouve notre siège social, nous sommes un {lien('déménageur à Seraing')} qui connaît {enumerer(c.villages)} comme ses propres rues. </>
    ),
    herstal: (c, lien) => (
      <>Et {lien('déménager à Herstal')}, à {c.distanceDepotKm} km, c'est revenir sur la commune historique de l'entreprise.</>
    ),
  },
  {
    verviers: (c, lien) => (
      <>Vers l'est, {lien('votre déménagement à Verviers')} se joue dans un bâti ancien à étages, où le monte-meubles est souvent la bonne réponse, à {c.tempsTrajetEstimeMin} minutes de route. </>
    ),
    ans: (c, lien) => (
      <>{lien('Nos déménageurs à Ans')} desservent {enumerer(c.villages)} à {c.distanceDepotKm} km du dépôt. </>
    ),
    oupeye: (c, lien) => (
      <>{lien('Un déménagement à Oupeye')}, à {c.distanceDepotKm} km, est le plus court de tous nos trajets. </>
    ),
    vise: (c, lien) => (
      <>Et {lien('déménager à Visé')}, en Basse-Meuse, demande {c.tempsTrajetEstimeMin} minutes de route.</>
    ),
  },
  {
    flemalle: (c, lien) => (
      <>En aval de Seraing, un {lien('déménagement à Flémalle')} s'organise depuis le dépôt en {c.tempsTrajetEstimeMin} minutes. </>
    ),
    huy: (c, lien) => (
      <>Plus loin, nous sommes aussi {lien('déménageur à Huy')}, entre Meuse et Condroz, à {c.distanceDepotKm} km. </>
    ),
    waremme: (_c, lien) => (
      <>Et nous groupons les interventions de Hesbaye sur une même journée, pour un {lien('déménagement à Waremme')} comme pour les villages qui l'entourent.</>
    ),
  },
];

/** Paragraphes du pilier : ce que l'intervention exige, commune par commune. */
const PILIER: Array<Record<string, Phrase>> = [
  {
    liege: (_c, lien) => (
      <>{lien('Déménageur à Liège')} depuis 1948, nous y traitons les kots d'étudiants comme les plateaux de bureaux, avec la réservation de voirie que l'hypercentre impose. </>
    ),
    seraing: (_c, lien) => (
      <>Un {lien('déménagement à Seraing')} se planifie depuis notre siège social, voie du Belvédère, avec une visite technique à très court délai. </>
    ),
    herstal: (c, lien) => (
      <>Un {lien('déménagement à Herstal')} part du dépôt en {c.tempsTrajetEstimeMin} minutes : c'est notre commune d'origine.</>
    ),
  },
  {
    verviers: (_c, lien) => (
      <>Comme {lien('déménageur à Verviers')}, nous prévoyons le monte-meubles pour les étages sans ascenseur du centre et réservons l'emplacement de voirie à l'avance. </>
    ),
    ans: (c, lien) => (
      <>Pour {lien('déménager à Ans')}, comptez {c.tempsTrajetEstimeMin} minutes depuis le dépôt. </>
    ),
    oupeye: (c, lien) => (
      <>Un {lien('déménagement à Oupeye')} part en {c.tempsTrajetEstimeMin} minutes. </>
    ),
    vise: (c, lien) => (
      <>Un {lien('déménagement à Visé')}, en Basse-Meuse, en demande {c.tempsTrajetEstimeMin}. Sur la première couronne, le trajet d'approche pèse peu sur le devis.</>
    ),
  },
  {
    flemalle: (c, lien) => (
      <>Pour {lien('déménager à Flémalle')}, comptez {c.tempsTrajetEstimeMin} minutes depuis le dépôt. </>
    ),
    huy: (c, lien) => (
      <>Un {lien('déménagement à Huy')}, à {c.distanceDepotKm} km, s'organise en tournée avec les autres adresses du secteur. </>
    ),
    waremme: (_c, lien) => (
      <>Une intervention de {lien('déménageur à Waremme')} suit la même logique : plusieurs adresses de Hesbaye le même jour, ce qui réduit la part du déplacement sur le devis.</>
    ),
  },
];

interface Props {
  variante: Variante;
  className?: string;
}

export default function CommunesPrioritaires({ variante, className = '' }: Props) {
  const publiees = new Map(communesPrioritaires().map((c) => [c.id, c]));
  const paragraphes = variante === 'accueil' ? ACCUEIL : PILIER;

  return (
    <div className={`space-y-4 text-muted text-[17px] leading-relaxed ${className}`}>
      {paragraphes.map((paragraphe, i) => {
        const phrases = Object.entries(paragraphe)
          .filter(([slug]) => publiees.has(slug))
          .map(([slug, phrase]) => {
            const c = publiees.get(slug)!;
            const lien = (ancre: string) => (
              <Link to={communeUrl(c)} className="text-navy font-bold underline hover:text-navy/70 transition-colors">
                {ancre}
              </Link>
            );
            return <span key={slug}>{phrase(c, lien)}</span>;
          });
        if (phrases.length === 0) return null;
        return <p key={i}>{phrases}</p>;
      })}
    </div>
  );
}
